import type { Account, AccountStatus, Balance } from "@/api/types";

export type AccountStatusView = {
  label: "Connecting" | "Connected" | "Stopped" | "Error";
  tone: AccountStatus;
};

export function accountStatusView(status?: string, enabled?: boolean): AccountStatusView {
  const s = (status || "").toLowerCase();
  if (s === "disabled" || enabled === false) return { label: "Stopped", tone: "stopped" };
  if (s === "connecting") return { label: "Connecting", tone: "connecting" };
  if (s === "running" || s === "active") return { label: "Connected", tone: "running" };
  if (s === "error" || s === "failed") return { label: "Error", tone: "error" };
  if (s === "stopped") return { label: "Stopped", tone: "stopped" };
  return { label: "Stopped", tone: "stopped" };
}

export function isFuturesAccount(account?: Pick<Account, "config"> | null): boolean {
  if (!account?.config) return false;
  const mt = String(account.config.market_type ?? account.config.marketType ?? "").toUpperCase();
  return mt === "FUTURES" || mt === "SWAP" || mt === "MARGIN" || mt === "PERP";
}

/** Short env/market label for titles, e.g. "Test/Futures". */
export function accountMarketLabel(account?: Pick<Account, "config"> | null): string {
  if (!account?.config) return "Spot";
  const cfg = account.config;
  const testish =
    cfg.testnet === true ||
    cfg.testnet === "true" ||
    cfg.sandbox === true ||
    cfg.sandbox === "true" ||
    String(cfg.environment ?? cfg.env ?? "").toLowerCase() === "testnet" ||
    String(cfg.environment ?? cfg.env ?? "").toLowerCase() === "test";
  const env = testish ? "Test" : "Live";
  const kind = isFuturesAccount(account) ? "Futures" : "Spot";
  return `${env}/${kind}`;
}

export function providerLabel(provider?: string): string {
  if (!provider) return "Unknown";
  const map: Record<string, string> = {
    binance: "Binance",
    bybit: "Bybit",
    okx: "OKX",
    kraken: "Kraken",
    gateio: "Gate.io",
    gate: "Gate.io",
    mexc: "MEXC",
    revolutx: "Revolut X",
  };
  return map[provider.toLowerCase()] || provider;
}

const STABLES = new Set(["USDT", "USDC", "USD", "FDUSD", "BUSD", "EUR"]);

/**
 * Account value in USD: the exchange's usd_value when sent, else stablecoin
 * balances only. Null when there is nothing to show (e.g. account not running).
 */
export function accountUsdBalance(balances?: Balance[] | null): { value: number; asset: string } | null {
  if (!balances?.length) return null;
  let usd = 0;
  let hasUsd = false;
  let stable = 0;
  let stableAsset = "";
  for (const b of balances) {
    const total = Number(b.free || 0) + Number(b.locked || 0);
    const v = Number(b.usd_value);
    if (b.usd_value != null && b.usd_value !== "" && Number.isFinite(v)) {
      usd += v;
      hasUsd = true;
    }
    if (STABLES.has(b.asset.toUpperCase()) && Number.isFinite(total)) {
      stable += total;
      if (!stableAsset || total > 0) stableAsset = b.asset.toUpperCase();
    }
  }
  if (hasUsd) return { value: usd, asset: "USD" };
  if (stableAsset) return { value: stable, asset: stableAsset };
  return null;
}

/** "Futures", "Spot", "Demo · Futures" from the account config. */
export function accountKindLabel(account?: Pick<Account, "config"> | null): string {
  const kind = isFuturesAccount(account) ? "Futures" : "Spot";
  const demo = String(account?.config?.network ?? "").toUpperCase() === "DEMO";
  return demo ? `Demo · ${kind}` : kind;
}
